import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input } from '@angular/core';
import { NbDialogRef } from '@nebular/theme';
import { finalize } from 'rxjs';
import {
  OrganisationService,
  PlanChangePreview,
} from '../../../services/organisation.service';

/** Confirmation for an upgrade carried out inside the portal.
 *
 *  Shown to subscriptions on a superseded price: Stripe's customer portal has
 *  no switch target for them, so the portal changes the subscription itself.
 *  Everything displayed comes from the backend preview (Stripe's own invoice
 *  preview) — nothing is calculated here. Closes with `true` after a
 *  successful, paid upgrade. */
@Component({
  selector: 'app-plan-upgrade-dialog',
  templateUrl: './plan-upgrade-dialog.component.html',
  styleUrl: './plan-upgrade-dialog.component.scss',
})
export class PlanUpgradeDialogComponent {
  @Input() planId!: string;
  /** i18n keys of the plan names (`products.<plan>.name`). */
  @Input() targetName!: string;
  @Input() currentName?: string;
  @Input() preview!: PlanChangePreview;

  accepted = false;
  /** Set when confirm is pressed without the acceptance tick. */
  acceptMissing = false;
  submitting = false;
  /** i18n key of the error shown above the footer. */
  errorKey?: string;
  /** The charge was declined — offer the way to the payment details. */
  paymentFailed = false;
  openingPortal = false;

  constructor(
    public dialogRef: NbDialogRef<PlanUpgradeDialogComponent>,
    private organisationService: OrganisationService
  ) {}

  get nextBillingDate(): Date | undefined {
    return this.preview.next_billing_at
      ? new Date(this.preview.next_billing_at * 1000)
      : undefined;
  }

  /** No German VAT on this customer's invoices (reverse charge, or seat
   *  outside the EU) — the VAT lines say so instead of showing 19 %. */
  get vatFree(): boolean {
    return (
      this.preview.tax_treatment === 'reverse_charge' ||
      this.preview.tax_treatment === 'non_eu'
    );
  }

  get intervalKey(): string {
    return this.preview.interval === 'year'
      ? 'components.productCard.perYear'
      : 'components.productCard.perMonth';
  }

  confirm() {
    if (this.submitting) {
      return;
    }
    if (!this.accepted) {
      this.acceptMissing = true;
      return;
    }
    this.submitting = true;
    this.errorKey = undefined;
    this.paymentFailed = false;
    this.organisationService
      .changePlan(this.planId, this.preview.proration_date)
      .pipe(finalize(() => (this.submitting = false)))
      .subscribe({
        next: () => this.dialogRef.close(true),
        error: (e: HttpErrorResponse) => {
          this.paymentFailed = e.status === 402;
          // 409: the preview is older than the backend accepts — the amount
          // shown may no longer be the one Stripe would charge.
          this.errorKey = this.paymentFailed
            ? 'dialogs.planUpgrade.paymentFailed'
            : e.status === 409
              ? 'dialogs.planUpgrade.previewExpired'
              : 'dialogs.planUpgrade.failed';
        },
      });
  }

  /** Straight to Stripe's customer portal to replace the card; it returns to
   *  Organisation › Abrechnung, from where the upgrade can be started again. */
  openPaymentDetails() {
    if (this.openingPortal) {
      return;
    }
    this.openingPortal = true;
    this.organisationService.openBillingPortal().subscribe({
      next: (url) => (window.location.href = url),
      error: () => (this.openingPortal = false),
    });
  }
}
